import request from '../request';
import reservations from '../../services/gma/reservations';
import solr from '../../services/solr';
import {setSolrHoursInitialDate} from '../solr';
import {cleanNonSessionReservationState} from './index';
import {batchActions} from '../../utils/redux';
import {getRentersAgeOptions, getAdditionalInfoSetOpts, getAdditionalInfoUnsetPath, formatTimeStamp} from '../../utils/app/reservations';
import {types} from '../../utils/js/types';
import {isArrayNotEmpty} from '../../utils/js/array';
import {get} from '../../utils/js/object';

import {GMI, STATE} from '../../constants';

const PICKUP = 'pickup';
const RETURN = 'return';
const {INITIATE} = STATE.UI.RESERVATIONS;

export const initiatePaths = {
  additional_information: INITIATE.concat(['additional_information']),
  booking_agent: INITIATE.concat(['booking_agent']),
  bundle_profile: INITIATE.concat(['bundle_profile']),
  car_class: INITIATE.concat(['car_class_code']),
  car_classes: INITIATE.concat(['car_class_codes']),
  contract: INITIATE.concat(['contract_number']),
  coupons: INITIATE.concat(['coupons']),
  iata_number: INITIATE.concat(['iata_number']),
  one_way_rental: INITIATE.concat(['one_way_rental']),
  partner_reward_program_code: INITIATE.concat(['partner_reward_program_code']),
  partner_reward_program_member_id: INITIATE.concat(['partner_reward_program_member_id']),
  partner_reward_program_name: INITIATE.concat(['partner_reward_program_name']),
  pickup_location: INITIATE.concat(['pickup_location']),
  pickup_location_id: INITIATE.concat(['pickup_location_id']),
  pickup_time: INITIATE.concat(['pickup_time']),
  preferred_car_class_codes: INITIATE.concat(['preferred_car_class_codes']),
  preferred_car_category_code: INITIATE.concat(['preferred_car_category_code']),
  product_code: INITIATE.concat(["product_code"]),
  renter_age: INITIATE.concat(['renter_age']),
  renter_age_label: INITIATE.concat(['renter_age_label']),
  return_location: INITIATE.concat(['return_location']),
  return_location_id: INITIATE.concat(['return_location_id']),
  return_time: INITIATE.concat(['return_time']),
  travel_purpose: INITIATE.concat(['travel_purpose']),
  update_profile_with_partner_rewards: INITIATE.concat(['update_profile_with_partner_rewards'])
};

/*************************** PRIVATE METHODS ***************************/

/**
 * @private
 * @param {Object} [opts] {exchangeType: String, id: String, state: Object}
 * @return {Object} {set: Boolean, value: Boolean}
 */
function formatOneWayRental(opts) {
  const {exchangeType, location, state} = opts;
  const {id, type} = location;

  const oneWayRentalValue = state.getIn(initiatePaths.one_way_rental);
  let expectedOneWayRentalValue;

  if (exchangeType) {
    const inverseExchangeType = exchangeType === PICKUP ? RETURN : PICKUP;
    expectedOneWayRentalValue = id !== state.getIn(initiatePaths[`${inverseExchangeType}_location_id`]);
  } else {
    expectedOneWayRentalValue = false;
  }

  return {
    // Only set value if it has changed and if the selected location isn't a city.
    set: oneWayRentalValue !== expectedOneWayRentalValue && type !== 'CITY',
    value: expectedOneWayRentalValue
  };
}

/**
 * @private
 * @desc Populate an array of actions based on the exchange type and the actions function passed in
 * @param {Function} actions
 * @param {Array|String} exchangeType
 * @returns {Array}
 */
function batchResInitiateByExchangeType(actions, exchangeType = [PICKUP, RETURN]) {
  let batchedActions = [];
  exchangeType = types(exchangeType).isArray ? exchangeType : [exchangeType];
  exchangeType.forEach((type, i) => batchedActions = batchedActions.concat(actions(type, i)));
  return batchedActions;
}

/**
 * @private
 * @param {Object} [opts] {location: Object, exchangeType: String, state: Object}
 * @returns {Array}
 */
function batchResInitiateLocation(opts) {
  const {location, exchangeType, state} = opts;
  const {id} = location;
  const oneWayRental = formatOneWayRental({exchangeType, location, state});

  const actions = (type, i) => {
    let nextActions = (oneWayRental.set && i === 0) ?
      [{
        type: GMI.SET_GMI_STATE,
        path: initiatePaths.one_way_rental,
        value: oneWayRental.value
      }] :
      [];

    nextActions = nextActions.concat([
      {type: GMI.SET_GMI_STATE, path: initiatePaths[`${type}_location`], value: location},
      {type: GMI.SET_GMI_STATE, path: initiatePaths[`${type}_location_id`], value: id}
    ]);

    if (type === PICKUP) {
      const next_options = getRentersAgeOptions(location, state.getIn(['gmi', 'solr', 'renter_age']));
      if (isArrayNotEmpty(next_options)) {
        const next_default_age = next_options.filter(option => option.selected)[0].value;
        const prev_renter_age = parseInt(state.getIn(['gmi', 'ui', 'reservations', 'initiate', 'renter_age']), 10);
        const next_renter_age = (
          !prev_renter_age || (prev_renter_age && next_options.filter(option => option.value === prev_renter_age).length === 0)
        ) ? next_default_age : null;
        if (next_renter_age !== null) {
          nextActions = nextActions.concat(setResInitiateRenterAge(next_renter_age));
        }
      }
    }

    return nextActions;
  };
  return batchResInitiateByExchangeType(actions, exchangeType);
}

/**
 * @private
 * @desc Set additional info by array of objects
 * @param {Array} value
 * @returns {Object} {type: string, path: (Array), value: (Array)}
 */
function setResAdditionalInfoBatchAction(value) {
  return {type: GMI.SET_GMI_STATE, path: initiatePaths.additional_information, value};
}

/**
 * @private
 * @desc Clear all additional info, or by id if present in state
 * @param {String} id
 * @returns {Function}
 */
function clearResInitiateAdditionalInfo(id) {
  return (dispatch, getState) => {
    const path = getAdditionalInfoUnsetPath({id, state: getState(), basePath: initiatePaths.additional_information});
    if (path) {
      dispatch({type: GMI.DELETE_GMI_STATE, path});
    }
  };
}

/**
 * @private
 * @param {Object} info {id: string, value: string}
 * @returns {Function}
 */
function setResInitiateAdditionalInfo(info) {
  return (dispatch, getState) => dispatch(Object.assign(
    {type: GMI.SET_GMI_STATE},
    getAdditionalInfoSetOpts({info, state: getState(), basePath: initiatePaths.additional_information})
  ));
}

/**
 * @private
 * @desc Clear the car class code info from the initiate object
 * @returns {Object} {type: string, path: []}
 */
function clearResInitiateCarClass() {
  return {type: GMI.DELETE_GMI_STATE, path: initiatePaths.car_class};
}

/**
 * @private
 * @param {String} code
 * @returns {Object} {type: string, path: [], value: string}
 */
function setResInitiateCarClass(code) {
  return {type: GMI.SET_GMI_STATE, path: initiatePaths.car_class, value: code};
}

/**
 * @private
 * @desc Clear the car class codes array from the initiate object
 * @returns {Object} {type: string, path: []}
 */
function clearResInitiateCarClasses() {
  return {type: GMI.DELETE_GMI_STATE, path: initiatePaths.car_classes};
}

/**
 * @private
 * @param {Array} codesArray
 * @returns {Object} {type: string, path: [], value: (Array)}
 */
function setResInitiateCarClasses(codesArray) {
  return {type: GMI.SET_GMI_STATE, path: initiatePaths.car_classes, value: codesArray};
}

/**
 * @private
 * @desc Clear the  Preferred car class code from the initiate objects
 * @return     {Object} {type: string, path: []}
 */
function clearResInitiatePreferredCarClassCodes() {
  return {type: GMI.SET_GMI_STATE, path: initiatePaths.preferred_car_class_codes};
}

/**
 * @private
 * @desc Set the preferred car class code for the initiate objects
 * @param       {string|array}  code    The car class code
 * @return      {Object}  `{type: string, path: [], value: string}`
 */
function setResInitiatePreferredCarClassCodes(codes){
  return {type: GMI.SET_GMI_STATE, path: initiatePaths.preferred_car_class_codes, value: codes};
}

/**
 * @private
 * @desc Clear the  Preferred car category code from the initiate objects
 * @return     {Object} {type: string, path: []}
 */
function clearResInitiatePreferredCarCategoryCode() {
  return {type: GMI.SET_GMI_STATE, path: initiatePaths.preferred_car_category_code};
}

/**
 * @private
 * @desc Set the preferred car category code for the initiate objects
 * @param       {string}  code    The car category code
 * @return      {Object}  `{type: string, path: [], value: string}`
 */
function setResInitiatePreferredCarCategoryCode(code){
  return {type: GMI.SET_GMI_STATE, path: initiatePaths.preferred_car_category_code, value: code}
}

/**
 * @private
 * @returns {Object} {type: string, path: []}
 */
function clearResInitiateContract() {
  return {type: GMI.DELETE_GMI_STATE, path: initiatePaths.contract};
}

/**
 * @private
 * @param {String} contractNumber
 * @returns {Object} {type: string, path: [], value: string}
 */
function setResInitiateContract(contractNumber) {
  return {type: GMI.SET_GMI_STATE, path: initiatePaths.contract, value: contractNumber};
}

/**
 * @private
 * @returns {Object} {type: string, path: []}
 */
function clearResInitiateCoupon(index = 0) {
  return {type: GMI.DELETE_GMI_STATE, path: initiatePaths.coupons.concat([index.toString()])};
}

/**
 * @private
 * @param {Object} [opts] {code: String, coupons: Array, index: Number, state: Object}
 * @returns {Object} {type: string, path: []}
 */
function setResInitiateCouponActions(opts) {
  const {code, coupons, index = 0, state} = opts;
  const initiateStateHasCoupons = !!state && types(state.getIn[initiatePaths.coupons]).isArray;
  const path = (coupons || !initiateStateHasCoupons) ?
    initiatePaths.coupons :
    initiatePaths.coupons.concat([index.toString()]);
  const value = coupons ? coupons :
    initiateStateHasCoupons ? code : [code];
  return {type: GMI.SET_GMI_STATE, path, value};
}

/**
 * @private
 * @param {String} code
 * @param {Number} index
 * @returns {Function}
 */
function setResInitiateCoupon(code, index) {
  return (dispatch, getState) => dispatch(setResInitiateCouponActions({code, index, state: getState()}));
}

/**
 * @private
 * @param {Object} [opts] {exchangeType: String, state: Object}
 * @returns {Array}
 */
function clearResInitiateLocationActions(opts) {
  const {exchangeType, state} = opts;
  const clearOneWayRental = (!exchangeType || exchangeType === RETURN) && state.getIn(initiatePaths.one_way_rental);

  const actions = (type, i) => {
    const base = (clearOneWayRental && i === 0) ?
      [{type: GMI.DELETE_GMI_STATE, path: initiatePaths.one_way_rental}] :
      [];

    return base.concat([
      {type: GMI.DELETE_GMI_STATE, path: initiatePaths[`${type}_location`]},
      {type: GMI.DELETE_GMI_STATE, path: initiatePaths[`${type}_location_id`]}
    ]);
  };

  return batchResInitiateByExchangeType(actions, exchangeType);
}

/**
 * @private
 * @returns {Function}
 */
function clearResInitiateLocation(exchangeType) {
  return (dispatch, getState) => dispatch(batchActions(...clearResInitiateLocationActions({exchangeType, state: getState()})));
}

/**
 * @private
 * @param {Object} location
 * @returns {Function}
 */
function ensureLocationDataExists(location) {
  return (dispatch, getState) => {
    const {id, type} = location;
    const state = getState();
    let storedPromise;
    if (type === 'CITY') {
      const country_code = get(location, 'address.country_code');
      const renterAge = state.getIn(STATE.SOLR.RENTER_AGE.concat([country_code])) || null;
      if (!renterAge) storedPromise = dispatch(request({data: {endpoint:{country: country_code}}, service: solr.renterAge}));
    } else {
      const hoursOfOperation = state.getIn(STATE.SOLR.HOURS.concat([id])) || null;
      if (!hoursOfOperation) {
        const solrHoursInitialDate = state.getIn(STATE.DATA.SOLR_HOURS_INITIAL_DATE);

        storedPromise = dispatch(request({
          data: {
            endpoint: {peopleSoftId: id},
            ...(solrHoursInitialDate && {
              params: {
                from: solrHoursInitialDate
              }
            })
          }, service: solr.hours}));
      }
    }
    return storedPromise ? storedPromise : Promise.resolve();
  };
}

/**
 * @private
 * @param {Object} location
 * @param {String} [exchangeType]
 * @returns {Function}
 */
function setResInitiateLocation(location = {}, exchangeType) {
  return (dispatch, getState) => dispatch(ensureLocationDataExists(location))
    .then(() => dispatch(batchActions(...batchResInitiateLocation({location, exchangeType, state: getState()}))));
}

/**
 * @private
 * @name clearResInitiateBookingAgent
 * @returns {Object} {type: string, path: []}
 */
function clearResInitiateBookingAgent() {
  return {type: GMI.DELETE_GMI_STATE, path: initiatePaths.booking_agent};
}

/**
 * @private
 * @name clearResInitiateRenterAge
 * @returns {Object} {type: string, path: []}
 */
function clearResInitiateRenterAge() {
  return {type: GMI.DELETE_GMI_STATE, path: initiatePaths.renter_age};
}

/**
 * @private
 * @name clearResInitiateRenterAgeLabel
 * @returns {Object} {type: string, path: []}
 */
function clearResInitiateRenterAgeLabel() {
  return {type: GMI.DELETE_GMI_STATE, path: initiatePaths.renter_age_label};
}

/**
 * @private
 * @name setResInitiateBookingAgent
 * @param {String} bookingAgentType
 * @returns {Object} {type: string, path: [], value: string}
 */
function setResInitiateBookingAgent(bookingAgentType) {
  return {
    type: GMI.SET_GMI_STATE,
    path: initiatePaths.booking_agent,
    value: bookingAgentType
  };
}

/**
 * @private
 * @name setResInitiateRenterAge
 * @param {String} age
 * @returns {Object} {type: string, path: [], value: string}
 */
function setResInitiateRenterAge(age) {
  return {type: GMI.SET_GMI_STATE, path: initiatePaths.renter_age, value: age};
}

/**
 * @private
 * @name setResInitiateRenterAgeLabel
 * @param {String} age
 * @returns {Object} {type: string, path: [], value: string}
 */
function setResInitiateRenterAgeLabel(age) {
  return {
    type: GMI.SET_GMI_STATE,
    path: initiatePaths.renter_age_label,
    value: age
  };
}

/**
 * @private
 * @param {String} exchangeType
 * @returns {Array}
 */
function clearResInitiateTimeStampActions(exchangeType) {
  const actions = type => [{type: GMI.DELETE_GMI_STATE, path: initiatePaths[`${type}_time`]}];
  return batchResInitiateByExchangeType(actions, exchangeType);
}

/**
 * @private
 * @name clearResInitiateTimeStamp
 * @returns {Function}
 */
function clearResInitiateTimeStamp(exchangeType) {
  return dispatch => dispatch(batchActions(...clearResInitiateTimeStampActions(exchangeType)));
}

/**
 * @private
 * @desc Return a function that takes the exchange type as a parameter,
 * that function returns the action needed to update the timestamp based on the current value of the timestamp in state
 * @param {Object} [opts] {state: Object, part: String}
 * @returns {Function}
 */
function clearResInitiateDateTimeAction(opts) {
  const {state, part = 'date'} = opts;
  return type => {
    const {ts, isNewTs} = formatTimeStamp({state, part, path: initiatePaths[`${type}_time`]});
    if (ts && isNewTs) {
      // If only the T is left in timestamp, delete from state, else update based on new value
      if (ts === 'T') {
        return {type: GMI.DELETE_GMI_STATE, path: initiatePaths[`${type}_time`]};
      } else {
        return {type: GMI.SET_GMI_STATE, path: initiatePaths[`${type}_time`], value: ts};
      }
    } else return {};
  };
}

/**
 * @private
 * @desc Create a function that can be used by batchResInitiateByExchangeType to determine the appropriate action to use when batching by exchange type,
 * based on the action function passed in
 * @param {Function} action
 * @param {String} exchangeType
 * @returns {Array}
 */
function clearResInitiateDateTimeActions(action, exchangeType) {
  const actions = type => [action(type)];
  return batchResInitiateByExchangeType(actions, exchangeType);
}

/**
 * @private
 * @desc DRY, Function is used to simplify clearResInitiateDate and clearResInitiateTime
 * Function dispatches batchActions which takes an array of actions returned from a function that gets destructured
 * @param {String} part
 * @param {String} exchangeType
 * @returns {Function}
 */
function batchClearResInitiateDateOrTime(part, exchangeType) {
  return (dispatch, getState) => {
    const action = clearResInitiateDateTimeAction({state: getState(), part, exchangeType});
    return dispatch(batchActions(...clearResInitiateDateTimeActions(action, exchangeType)));
  };
}

/**
 * @private
 * @name clearResInitiateDate
 * @desc Clear reservation initiate timestamp by date part and exchange type
 * @param {String} exchangeType
 * @returns {Function}
 */
function clearResInitiateDate(exchangeType) {
  return dispatch => dispatch(batchClearResInitiateDateOrTime('date', exchangeType));
}

/**
 * @private
 * @name clearResInitiateTime
 * @desc Clear reservation initiate timestamp by time part and exchange type
 * @param {String} exchangeType
 * @returns {Function}
 */
function clearResInitiateTime(exchangeType) {
  return dispatch => dispatch(batchClearResInitiateDateOrTime('time', exchangeType));
}

/**
 * @private
 * @name setResInitiateTimeStamp
 * @param {String} timeStamp
 * @param {String} exchangeType
 * @returns {Object}
 */
function setResInitiateTimeStamp(timeStamp, exchangeType) {
  return {type: GMI.SET_GMI_STATE, path: initiatePaths[`${exchangeType}_time`], value: timeStamp};
}

/**
 * @private
 * @desc DRY, Function is used to simplify setResInitiateDate and setResInitiateTime
 * @param {Object} opts {timeStamp, part, exchangeType}
 * @returns {Function}
 */
function batchResInitiateDateOrTime(opts) {
  const {timeStamp, part, exchangeType} = opts;
  return (dispatch, getState) => {
    const value = formatTimeStamp({ts:timeStamp, state: getState(), part, path: initiatePaths[`${exchangeType}_time`]}).ts;
    dispatch({type: GMI.SET_GMI_STATE, path: initiatePaths[`${exchangeType}_time`], value});
  };
}

/**
 * @private
 * @name setResInitiateDate
 * @desc Set reservation initiate timestamp by date part and exchange type
 * @param {String} timeStamp
 * @param {String} exchangeType
 * @returns {Function}
 */
function setResInitiateDate(timeStamp, exchangeType) {
  return dispatch => dispatch(batchResInitiateDateOrTime({timeStamp, part: 'date', exchangeType}));
}

/**
 * @private
 * @name setResInitiateTime
 * @desc Set reservation initiate timestamp by time part and exchange type
 * @param {String} timeStamp
 * @param {String} exchangeType
 * @returns {Function}
 */
function setResInitiateTime(timeStamp, exchangeType) {
  return dispatch => dispatch(batchResInitiateDateOrTime({timeStamp, part: 'time', exchangeType}));
}

/**
 * @private
 * @name setResInitiateBundleProfile
 * @desc Set reservation initiate bundle_profile
 * @param {String} bundle_profile
 * @returns {Function}
 */
function setResInitiateBundleProfile(bundle_profile) {
  return dispatch => dispatch({type: GMI.SET_GMI_STATE, path: initiatePaths.bundle_profile, value: bundle_profile});
}

/**
 * @private
 * @name clearResInitiateBundleProfile
 * @returns {Object} {type: string, path: []}
 */
function clearResInitiateBundleProfile() {
  return {type: GMI.DELETE_GMI_STATE, path: initiatePaths.bundle_profile};
}

/**
 * @private
 * @name setResInitiatePartnerRewardsCode
 * @desc Set reservation initiate partner_reward_program_code
 * @param {String} code
 * @returns {Function}
 */
function setResInitiatePartnerRewardsCode(code) {
  return dispatch => dispatch({type: GMI.SET_GMI_STATE, path: initiatePaths.partner_reward_program_code, value: code});
}

/**
 * @private
 * @name clearResInitiatePartnerRewardsCode
 * @returns {Object} {type: string, path: []}
 */
function clearResInitiatePartnerRewardsCode() {
  return {type: GMI.DELETE_GMI_STATE, path: initiatePaths.partner_reward_program_code};
}

/**
 * @private
 * @name setResInitiatePartnerRewardsMemberId
 * @desc Set reservation initiate partner_reward_program_member_id
 * @param {String} memberId
 * @returns {Function}
 */
function setResInitiatePartnerRewardsMemberId(memberId) {
  return dispatch => dispatch({type: GMI.SET_GMI_STATE, path: initiatePaths.partner_reward_program_member_id, value: memberId});
}

/**
 * @private
 * @name clearResInitiatePartnerRewardsMemberId
 * @returns {Object} {type: string, path: []}
 */
function clearResInitiatePartnerRewardsMemberId() {
  return {type: GMI.DELETE_GMI_STATE, path: initiatePaths.partner_reward_program_member_id};
}

/**
 * @private
 * @name setResInitiatePartnerRewardsName
 * @desc Set reservation initiate partner_reward_program_name
 * @param {String} name
 * @returns {Function}
 */
function setResInitiatePartnerRewardsName(name) {
  return {type: GMI.SET_GMI_STATE, path: initiatePaths.partner_reward_program_name, value: name};
}

/**
 * @private
 * @name clearResInitiatePartnerRewardsName
 * @returns {Object} {type: string, path: []}
 */
function clearResInitiatePartnerRewardsName() {
  return {type: GMI.DELETE_GMI_STATE, path: initiatePaths.partner_reward_program_name};
}

/**
 * @private
 * @name setUpdateProfileWithPartnerRewardsFlag
 * @desc Set reservation initiate update_profile_with_partner_rewards flag to GMA attach PRP to user profile
 * @param {boolean} value
 * @returns {Function}
 */
function setUpdateProfileWithPartnerRewardsFlag(value) {
  return {type: GMI.SET_GMI_STATE, path: initiatePaths.update_profile_with_partner_rewards, value};
}

/**
 * @private
 * @name clearUpdateProfileWithPartnerRewardsFlag
 * @returns {Object} {type: string, path: []}
 */
function clearUpdateProfileWithPartnerRewardsFlag() {
  return {type: GMI.DELETE_GMI_STATE, path: initiatePaths.update_profile_with_partner_rewards};
}

/**
 * @private
 * @name setResInitiateIataNumber
 * @desc Set reservation initiate iata_number
 * @param {String} value
 * @returns {Function}
 */
function setResInitiateIataNumber(value) {
  return dispatch => dispatch({type: GMI.SET_GMI_STATE, path: initiatePaths.iata_number, value});
}

/**
 * @private
 * @name clearResInitiateIataNumber
 * @returns {Object} {type: string, path: []}
 */
function clearResInitiateIataNumber() {
  return {type: GMI.DELETE_GMI_STATE, path: initiatePaths.iata_number};
}

/**
 * @private
 * @name clearResInitiateTravelPurpose
 */
function clearResInitiateTravelPurpose() {
  return {type: GMI.DELETE_GMI_STATE, path: initiatePaths.travel_purpose};
}

/**
 * @private
 * @name clearResInitiateState
 * @returns {Object} {type: string, path: [], value: object}
 */
function clearResInitiateState() {
  return {type: GMI.SET_GMI_STATE, path: INITIATE, value: {}};
}

/**
 * @private
 * @name setResInitiateProductCodeActions
 * @param {string} productCode
 * @returns {Object} { type: string, path: [], value: string }
 */
function setResInitiateProductCodeActions(productCode) {
  return {
    type: GMI.SET_GMI_STATE,
    path: initiatePaths.product_code,
    value: productCode
  };
}

/**
 * @private
 * @name setResInitiateProductCode
 * @desc Set reservation initiate product_code flag to GMA
 * @param {string} productCode
 * @return {Function}
 */
function setResInitiateProductCode(productCode) {
  return (dispatch) => dispatch(setResInitiateProductCodeActions(productCode));
}

/**
* @private
* @returns {Object} {type: string, path: []}
*/
function clearResInitiateProductCode() {
  return {
    type: GMI.DELETE_GMI_STATE,
    path: initiatePaths.product_code
  };
}

/**
 * @private
 * @name setResInitiateTravelPurpose
 * @param {String} travelPurpose
 */
function setResInitiateTravelPurpose(travelPurpose) {
  return {type: GMI.SET_GMI_STATE, path: initiatePaths.travel_purpose, value: travelPurpose};
}

/**
 * @private
 * @param {Object} [args]
 * @returns {Function}
 * ECR-29874 if args comes in as null, we do not get the empty
 * object to destructure from
 */
function setResInitiateState(args = {}) {
  return dispatch => {
    const {
      booking_agent,
      car_class_code,
      car_class_codes,
      contract_number,
      coupons,
      product_code,
      one_way_rental,
      pickup_location,
      pickup_time,
      renter_age,
      renter_age_label,
      return_location,
      return_time,
      additional_information,
      bundle_profile,
      partner_reward_program_code,
      partner_reward_program_member_id,
      partner_reward_program_name,
      preferred_car_class_codes,
      preferred_car_category_code,
      update_profile_with_partner_rewards,
      iata_number
    } = args;

    const batchedActions = [];
    if (types(additional_information).isArray) batchedActions.push(setResAdditionalInfoBatchAction(additional_information));
    if (booking_agent) batchedActions.push(setResInitiateBookingAgent(booking_agent));
    if (pickup_time) batchedActions.push(setResInitiateTimeStamp(pickup_time, PICKUP));
    if (return_time) batchedActions.push(setResInitiateTimeStamp(return_time, RETURN));
    if (renter_age) batchedActions.push(setResInitiateRenterAge(renter_age));
    if (renter_age_label) batchedActions.push(setResInitiateRenterAgeLabel(renter_age_label));
    if (contract_number) batchedActions.push(setResInitiateContract(contract_number));
    if (coupons) batchedActions.push(setResInitiateCouponActions({coupons}));
    if (product_code) batchedActions.push(setResInitiateProductCodeActions(product_code));
    if (car_class_code) batchedActions.push(setResInitiateCarClass(car_class_code));
    if (car_class_codes) batchedActions.push(setResInitiateCarClasses(car_class_codes));
    if (bundle_profile) batchedActions.push(setResInitiateBundleProfile(bundle_profile));
    if (partner_reward_program_code) batchedActions.push(setResInitiatePartnerRewardsCode(partner_reward_program_code));
    if (partner_reward_program_member_id) batchedActions.push(setResInitiatePartnerRewardsMemberId(partner_reward_program_member_id));
    if (partner_reward_program_name) batchedActions.push(setResInitiatePartnerRewardsName(partner_reward_program_name));
    if (preferred_car_class_codes) batchedActions.push(setResInitiatePreferredCarClassCodes(preferred_car_class_codes));
    if (preferred_car_category_code) batchedActions.push(setResInitiatePreferredCarCategoryCode(preferred_car_category_code));
    if (typeof update_profile_with_partner_rewards !== 'undefined') batchActions.push(setUpdateProfileWithPartnerRewardsFlag(update_profile_with_partner_rewards));
    if (iata_number) batchedActions.push(setResInitiateIataNumber(iata_number));
    if (batchedActions.length) dispatch(batchActions(...batchedActions));

    if (one_way_rental) {
      const promises = [];
      if (pickup_location) promises.push(dispatch(setResInitiateLocation(pickup_location, PICKUP)));
      if (return_location) promises.push(dispatch(setResInitiateLocation(return_location, RETURN)));
      if (promises.length) return Promise.all(promises);
    } else if (pickup_location) {
      return dispatch(setResInitiateLocation(pickup_location));
    }
    return Promise.resolve(args);
  };
}

const ADDITIONAL_INFORMATION = 'additional_information';
const BOOKING_AGENT = 'booking_agent';
const CAR_CLASS = 'car_class';
const CAR_CLASSES = 'car_classes';
const CONTRACT = 'contract';
const COUPON = 'coupon';
const LOCATION = 'location';
const RENTER_AGE = 'renter_age';
const RENTER_AGE_LABEL = 'renter_age_label';
const TIMESTAMP = 'timestamp';
const DATE = 'date';
const TIME = 'time';
const BUNDLE_PROFILE = 'bundle_profile';
const PARTNER_REWARD_PROGRAM_CODE = 'partner_reward_program_code';
const PARTNER_REWARD_PROGRAM_MEMBER_ID = 'partner_reward_program_member_id';
const PARTNER_REWARD_PROGRAM_NAME = 'partner_reward_program_name';
const PREFERRED_CAR_CLASS_CODES = 'preferred_car_class_codes';
const PREFERRED_CAR_CATEGORY_CODE = 'preferred_car_category_code';
const UPDATE_PROFILE_WITH_PARTNER_REWARDS = 'update_profile_with_partner_rewards';
const PRODUCT_CODE = "product_code";
const IATA_NUMBER = "iata_number";
const TRAVEL_PURPOSE = 'travel_purpose';
const SOLR_HOURS_INITIAL_DATE = 'solr_hours_initial_date';

/*************************** PUBLIC METHODS ***************************/

export const initiateState = {
  /**
   * @name set
   * @desc A global setter for all reservation.initiate payload data
   * @param {String} [type]
   * @param {Object} [args]
   * @returns {Function}
   * @memberof actions.reservations.initiateState
   * @example
   *
   * // set additional information
   * store.dispatch(actions.reservations.initiateState.set('additional_information', {additional_information:{id: "pin", value: "12345"}}));
   *
   * // set booking agent
   * store.dispatch(actions.reservations.initiateState.set('booking_agent', {booking_agent:'BOOKING_ON_BEHALF_OF'}));
   *
   * // set car class
   * store.dispatch(actions.reservations.initiateState.set('car_class', {code:'ECAR'}));
   *
   * // set car classes array
   * store.dispatch(actions.reservations.initiateState.set('car_classes', {codes: ['ECAR', 'PCAR']}));
   *
   * // set contract
   * store.dispatch(actions.reservations.initiateState.set('contract', {contract_number:'govbiz'}));
   *
   * // set coupon
   * store.dispatch(actions.reservations.initiateState.set('coupon', {code:'ATESTKB3', index:1}));
   *
   * // set product_code
   * store.dispatch(actions.reservations.initiateState.set('product_code', 'MCO3'));
   *
   * // set location
   * // location is a solr/gbo location object
   * // exchangeType. optional. can be 'pickup', 'return' or omitted for round trip
   * store.dispatch(actions.reservations.initiateState.set('location', {location, exchangeType:'pickup'}));
   *
   * // set renter age
   * store.dispatch(actions.reservations.initiateState.set('renter_age', {renter_age:'25'}));
   *
   * // set renter age label
   * store.dispatch(actions.reservations.initiateState.set('renter_age_label', {renter_age_label:'25+'}));
   *
   * // set timestamp
   * // exchangeType: 'pickup' or 'return'
   * store.dispatch(actions.reservations.initiateState.set('timestamp', {timestamp:'2018-05-21T18:30', exchangeType:'pickup'}));
   *
   * // set date
   * // exchangeType: 'pickup' or 'return'
   * store.dispatch(actions.reservations.initiateState.set('date', {timestamp:'2018-05-21', exchangeType:'pickup'}));
   *
   * // set time
   * // exchangeType: 'pickup' or 'return'
   * store.dispatch(actions.reservations.initiateState.set('time', {timestamp:'18:30', exchangeType:'pickup'}));
   *
   * // set bundle profile
   * store.dispatch(actions.reservations.initiateState.set('bundle_profile', {bundle_profile:'aluk homepage'}));
   *
   * // set preferred car class code presets
   * store.dispatch(actions.reservations.initiateState.set('preferred_car_class_codes', {code_classes: '100'}));
   * store.dispatch(actions.reservations.initiateState.set('preferred_car_class_codes', {code_classes: ['100']}));
   *
   * // set preferred car category code presets
   * store.dispatch(actions.reservations.initiateState.set('preferred_car_category_code', {code_category: 'Luxury'}));
   *
   * // set partner reward program code
   * store.dispatch(actions.reservations.initiateState.set('partner_reward_program_code', {partner_reward_program_code:'CA'}));
   *
   * // set partner reward program member ID
   * store.dispatch(actions.reservations.initiateState.set('partner_reward_program_member_id', {partner_reward_program_member_id:'1489DCT78'}));
   *
   * // set update profile with partner rewards flag
   * store.dispatch(actions.reservations.initiateState.set('update_profile_with_partner_rewards', {update_profile_with_partner_rewards: true}));
   *
   * // set iata number
   * store.dispatch(actions.reservations.initiateState.set('iata_number', 'AL000520'));
   *
   */
  set(type = null, args) {
    return dispatch => {
      if (types(type).isString) {
        if(type === ADDITIONAL_INFORMATION) {
          dispatch(setResInitiateAdditionalInfo(args.additional_information));
        } else if (type === BOOKING_AGENT) {
          dispatch(setResInitiateBookingAgent(args.booking_agent));
        } else if (type === CAR_CLASS) {
          dispatch(setResInitiateCarClass(args.code));
        } else if (type === CAR_CLASSES) {
          dispatch(setResInitiateCarClasses(args.codes));
        } else if (type === CONTRACT) {
          dispatch(setResInitiateContract(args.contract_number));
        } else if (type === COUPON) {
          dispatch(setResInitiateCoupon(args.code, args.index));
        } else if (type === PRODUCT_CODE) {
          dispatch(setResInitiateProductCode(args.product_code));
        } else if (type === SOLR_HOURS_INITIAL_DATE) {
          dispatch(setSolrHoursInitialDate(args.solr_hours_initial_date));
        } else if (type === LOCATION) {
          // return a promise
          return dispatch(setResInitiateLocation(args.location, args.exchangeType));
        } else if (type === RENTER_AGE) {
          dispatch(setResInitiateRenterAge(args.renter_age));
        } else if (type === RENTER_AGE_LABEL) {
          dispatch(setResInitiateRenterAgeLabel(args.renter_age_label));
        } else if (type === TIMESTAMP) {
          dispatch(setResInitiateTimeStamp(args.timestamp, args.exchangeType));
        } else if (type === DATE) {
          dispatch(setResInitiateDate(args.timestamp, args.exchangeType));
        } else if (type === TIME) {
          dispatch(setResInitiateTime(args.timestamp, args.exchangeType));
        } else if (type === BUNDLE_PROFILE) {
          dispatch(setResInitiateBundleProfile(args.bundle_profile));
        } else if (type === PARTNER_REWARD_PROGRAM_CODE) {
          dispatch(setResInitiatePartnerRewardsCode(args.partner_reward_program_code));
        } else if (type === PARTNER_REWARD_PROGRAM_MEMBER_ID) {
          dispatch(setResInitiatePartnerRewardsMemberId(args.partner_reward_program_member_id));
        } else if (type === PARTNER_REWARD_PROGRAM_NAME) {
          dispatch(setResInitiatePartnerRewardsName(args.partner_reward_program_name));
        } else if (type === PREFERRED_CAR_CLASS_CODES) {
          dispatch(setResInitiatePreferredCarClassCodes(args.code_classes));
        } else if (type === PREFERRED_CAR_CATEGORY_CODE) {
          dispatch(setResInitiatePreferredCarCategoryCode(args.code_category));
        } else if (type === UPDATE_PROFILE_WITH_PARTNER_REWARDS) {
          dispatch(setUpdateProfileWithPartnerRewardsFlag(args.update_profile_with_partner_rewards));
        } else if (type === IATA_NUMBER) {
          dispatch(setResInitiateIataNumber(args.iata_number));
        } else if(type === TRAVEL_PURPOSE) {
          dispatch(setResInitiateTravelPurpose(args.travel_purpose));
        } else {
          console.warn(`${type} is not a supported state type`);
        }
      } else {
        // return a promise
        return dispatch(setResInitiateState(args));
      }
    };
  },

  /**
   * @name unset
   * @desc A global unsetter for all reservation.initiate payload data
   * @param {String} [type]
   * @param {Object} [args]
   * @returns {Function}
   * @memberof actions.reservations.initiateState
   * @example
   *
   * // clear additional information
   * store.dispatch(actions.reservations.initiateState.unset('additional_information'));
   *
   * // clear booking agent
   * store.dispatch(actions.reservations.initiateState.unset('booking_agent'));
   *
   * // clear car class
   * store.dispatch(actions.reservations.initiateState.unset('car_class'));
   *
   * // clear contract
   * store.dispatch(actions.reservations.initiateState.unset('contract'));
   *
   * // clear coupon
   * store.dispatch(actions.reservations.initiateState.unset('coupon', {index:1}));
   *
   * // clear product_code
   * store.dispatch(actions.reservations.initiateState.unset('product_code'));
   *
   * // clear location
   * // exchangeType. optional. can be 'pickup', 'return' or omitted for round trip
   * store.dispatch(actions.reservations.initiateState.unset('location', {exchangeType:'pickup'}));
   *
   * // clear renter age
   * store.dispatch(actions.reservations.initiateState.unset('renter_age'));
   *
   * // clear renter age label
   * store.dispatch(actions.reservations.initiateState.unset('renter_age_label'));
   *
   * // clear timestamp
   * // exchangeType: 'pickup' or 'return'
   * store.dispatch(actions.reservations.initiateState.unset('timestamp', {exchangeType:'pickup'}));
   *
   * // clear date
   * // exchangeType: 'pickup' or 'return'
   * store.dispatch(actions.reservations.initiateState.unset('date', {exchangeType:'pickup'}));
   *
   * // clear time
   * // exchangeType: 'pickup' or 'return'
   * store.dispatch(actions.reservations.initiateState.unset('time', {exchangeType:'pickup'}));
   *
   * // clear bundle profile
   * store.dispatch(actions.reservations.initiateState.unset('bundle_profile'));
   *
   * // clear partner reward program code
   * store.dispatch(actions.reservations.initiateState.unset('partner_reward_program_code'));
   *
   * // clear preferred car class code presets
   * store.dispatch(actions.reservations.initiateState.unset('preferred_car_class_codes'));
   *
   * // clear preferred car category code presets
   * store.dispatch(actions.reservations.initiateState.unset('preferred_car_category_code'));
   *
   * // clear partner reward program member ID
   * store.dispatch(actions.reservations.initiateState.unset('partner_reward_program_member_id'));
   *
   * // clear flag to update profile with partner rewards
   * store.dispatch(actions.reservations.initiateState.unset('update_profile_with_partner_rewards'));
   *
   * // clear iata number
   * store.dispatch(actions.reservations.initiateState.unset('iata_number'));
   */
  unset(type = null, args = {}) {
    return dispatch => {
      if (types(type).isString) {
        if (type === ADDITIONAL_INFORMATION) {
          dispatch(clearResInitiateAdditionalInfo(args.id));
        } else if (type === BOOKING_AGENT) {
          dispatch(clearResInitiateBookingAgent());
        } else if (type === CAR_CLASS) {
          dispatch(clearResInitiateCarClass());
        } else if (type === CAR_CLASSES) {
          dispatch(clearResInitiateCarClasses());
        } else if (type === CONTRACT) {
          dispatch(clearResInitiateContract());
        } else if (type === COUPON) {
          dispatch(clearResInitiateCoupon(args.index));
        } else if (type === PRODUCT_CODE) {
          dispatch(clearResInitiateProductCode());
        } else if (type === LOCATION) {
          dispatch(clearResInitiateLocation(args.exchangeType));
        } else if (type === RENTER_AGE) {
          dispatch(clearResInitiateRenterAge());
        } else if (type === RENTER_AGE_LABEL) {
          dispatch(clearResInitiateRenterAgeLabel());
        } else if (type === TIMESTAMP) {
          dispatch(clearResInitiateTimeStamp(args.exchangeType));
        } else if (type === DATE) {
          dispatch(clearResInitiateDate(args.exchangeType));
        } else if (type === TIME) {
          dispatch(clearResInitiateTime(args.exchangeType));
        } else if (type === BUNDLE_PROFILE) {
          dispatch(clearResInitiateBundleProfile());
        } else if (type === PARTNER_REWARD_PROGRAM_CODE) {
          dispatch(clearResInitiatePartnerRewardsCode());
        } else if (type === PARTNER_REWARD_PROGRAM_MEMBER_ID) {
          dispatch(clearResInitiatePartnerRewardsMemberId());
        } else if (type === PARTNER_REWARD_PROGRAM_NAME) {
          dispatch(clearResInitiatePartnerRewardsName());
        } else if (type === PREFERRED_CAR_CLASS_CODES) {
          dispatch(clearResInitiatePreferredCarClassCodes());
        } else if (type === PREFERRED_CAR_CATEGORY_CODE) {
          dispatch(clearResInitiatePreferredCarCategoryCode());
        } else if (type === UPDATE_PROFILE_WITH_PARTNER_REWARDS) {
          dispatch(clearUpdateProfileWithPartnerRewardsFlag());
        } else if (type === IATA_NUMBER) {
          dispatch(clearResInitiateIataNumber());
        } else if(type === TRAVEL_PURPOSE) {
          dispatch(clearResInitiateTravelPurpose());
        } else {
          console.warn(`${type} is not a supported state type`);
        }
      } else {
        dispatch(clearResInitiateState());
      }
    };
  }
};

/**
 * @name initiate
 * @desc Make the GMA reservations/initiate call with state data; clear previous non-LDT res data on success
 * @param {Object} [opts] {
 *   cleanCommitState: true,
 *   cleanGmaReservationState: true,
 *   cleanInitiateState: false,
 *   cleanVehicleFilters: true,
 *   cleanDriverProfile: false
 * }
 * @param {Object} [data] {params} Will get merged with getState().getIn(INITIATE).toJS() object
 * @param {Object} [requestOpts]
 * @returns {Function}
 * @memberof actions.reservations
 * @example
 * store.dispatch(actions.reservations.initiate());
 */
export function initiate(opts = {}, data, requestOpts = {}) {
  const cleanState = Object.assign({
    cleanCarClassState: true,
    cleanCommitState: true,
    cleanExtrasState: true,
    cleanGmaReservationState: true,
    cleanInitiateState: false,
    cleanVehicleFilters: true,
    cleanDriverProfile: true
  }, opts);
  return (dispatch, getState) => {
    const gmiPayload = getState().getIn(INITIATE).toJS();
    if (!types(data).isObject) data = {};
    return dispatch(request({
      data: {
        params: data?.params,
        payload: {...gmiPayload, ...data?.payload}
      },
      service: reservations.initiate,
      opts: requestOpts
    })).then(resp => {
      if (!isArrayNotEmpty(resp.messages)) dispatch(cleanNonSessionReservationState(cleanState));
      return resp;
    });
  };
}
